import { useCallback } from 'react';
import type { TableColumns, ValueLists } from '@ohpen/enso-ui';
import { productsSqe, type ProductsSqeRequest } from '@pmp/api';
import { useSqeList } from '@pmp/hooks';
import { ProductData } from '@pmp/types';
import type { SqeNode, SqePage, SqeQuery } from '@pmp/utils';

import { productSqeFields } from './tableConfig';
import { flattenProductAdditionalFields } from './utils';

const endsOnOrAfter = (day: string): SqeNode => ({
  type: 'group',
  op: 'or',
  nodes: [
    { type: 'predicate', field: 'endDate', op: 'isNull' },
    { type: 'predicate', field: 'endDate', op: 'gte', value: day },
  ],
});

// Today as the UTC calendar day.
const runningToday = (): SqeNode[] => {
  const today = new Date().toISOString().slice(0, 10);
  return [{ type: 'predicate', field: 'startDate', op: 'lte', value: today }, endsOnOrAfter(today)];
};

const fetchProducts = async (query: SqeQuery, activeToday: boolean, signal?: AbortSignal): Promise<SqePage<ProductData>> => {
  const nodes = [...(query.filter ? [query.filter] : []), ...(activeToday ? runningToday() : [])];
  const body = { ...query, filter: { type: 'group', op: 'and', nodes } } as ProductsSqeRequest;
  const { data } = await productsSqe({ body, signal });
  return { items: flattenProductAdditionalFields(data.items), total: data.total };
};

/**
 * The products with these codes, matched without case. The search's `in` compares codes exactly, so each code is
 * asked for on its own with `startsWith`, which does not, sorted by code: the search sorts text by its lower case,
 * so a code comes before every longer one it starts, and the first row is the code itself if the list holds it.
 */
export const fetchProductsByCode = async (codes: readonly string[], activeToday: boolean, signal?: AbortSignal): Promise<ProductData[]> => {
  const firsts = await Promise.all(
    codes.map(code =>
      fetchProducts(
        {
          filter: { type: 'predicate', field: 'code', op: 'startsWith', value: code },
          sort: [{ field: 'code', dir: 'asc' }],
          page: 1,
          pageSize: 1,
        },
        activeToday,
        signal,
      ),
    ),
  );
  return firsts.flatMap(({ items }, index) => items.filter(product => product.code?.toLowerCase() === codes[index].toLowerCase()));
};

export const useProductsSqe = (columns: TableColumns<ProductData>, valueLists: ValueLists | undefined, activeToday: boolean) => {
  const fetchProductsPage = useCallback((query: SqeQuery) => fetchProducts(query, activeToday), [activeToday]);

  // An export holds every product the list holds, however many there are.
  return useSqeList(fetchProductsPage, columns, productSqeFields, valueLists, undefined, Number.POSITIVE_INFINITY);
};
